import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-apollo';

import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import { IdentityForJerseyColors } from '../../graphql/queries/identity';
import { identityHierarchyJerseyColorsSet as identityHierarchyJerseyColorsSetMutation } from '../../../../../../../graphql/mutations/identity';
import { getAsset } from '../../../../../../../util/assets';
import { ALBUM_FOLDER_UNIFORM_TAG_JERSEY_COLOR } from '../../../../../../../constants/album-folder-tags';
import { capitalize } from '../../../../../../../util/text';
import Label from '../../../../../../../components/global/Label';
import Button from '../../../../../../../components/global/Button';

const propTypes = {
  identityId: PropTypes.string,
  albumFolderId: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired,
};

const JerseyColorSelector = ({ identityId, albumFolderId, onRequestClose }) => {
  const openAlert = useOpenAlert();
  const [color, setColor] = useState('');
  const [saving, setSaving] = useState(false);
  const [identityHierarchyJerseyColorsSet] = useMutation(
    identityHierarchyJerseyColorsSetMutation,
  );

  const colorOptions = useMemo(
    () =>
      Object.values(ALBUM_FOLDER_UNIFORM_TAG_JERSEY_COLOR).map(jerseyColor =>
        jerseyColor.toUpperCase(),
      ),
    [],
  );

  const onIdentityHierarchyJerseyColorsSet = async input => {
    if (saving) {
      return;
    }

    setSaving(true);

    try {
      await identityHierarchyJerseyColorsSet({
        variables: {
          input: {
            identity_id: identityId,
            hierarchy_item_id: albumFolderId,
            ...input,
          },
        },
        refetchQueries: [
          {
            query: IdentityForJerseyColors,
            variables: {
              id: identityId,
              album_folder_id: albumFolderId,
            },
          },
        ],
      });
      onRequestClose();
    } catch (e) {
      openAlert(getErrorString(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <div className="text-center mb-2">
        <img src={getAsset('/waldo-icon.svg')} width="40" alt="Waldo Icon" />
      </div>
      <h4 className="text-center">
        Pick the jersey or uniform color worn in your photos and we&apos;ll
        customize your products!
      </h4>

      <div>
        <Label id="grade">Jersey or Uniform Color</Label>
        <select
          id="color"
          name="color"
          value={color}
          className="form-control"
          onChange={({ target: { value } }) => setColor(value)}
          placeholder="Select..."
        >
          <option value="">Select...</option>
          {colorOptions.map(jerseyColor => (
            <option key={jerseyColor} value={jerseyColor}>
              {capitalize(jerseyColor)}
            </option>
          ))}
        </select>
      </div>
      <Button
        btnBlock
        disabled={!color || saving}
        className="mt-2"
        onClick={() => {
          onIdentityHierarchyJerseyColorsSet({
            create_jersey_colors: [{ jersey_color: color.toUpperCase() }],
          });
        }}
      >
        {saving ? 'Saving...' : 'Save'}
      </Button>
      <Button
        btnBlock
        btnStyle="link"
        disabled={saving}
        onClick={() => onRequestClose()}
        className="mt-2 text-extra-bold text-center text-xs text-color-base"
      >
        Skip this
      </Button>
    </Fragment>
  );
};

JerseyColorSelector.propTypes = propTypes;

export default JerseyColorSelector;
